import { Checkbox, Radio } from '@feature/Common'
import Scrollable from '@feature/Scrollable'
import { Collapse, FormControlLabel, FormGroup, LinearProgress, TextField, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useAppDispatch } from 'app/hooks'
import { filterTermSelected, termSearchChanged } from 'app/thunks'
import React from 'react'
import { TaxonomyId, TaxonomyState, TermId, TermTree } from 'types'
import { SkeletonTerms } from './TaxonomyChoicesSkeletons'

type TaxonomyChoicesProps<T extends TaxonomyId> = TaxonomyState<T> & {
  hasParent?: boolean
}

const TaxonomyChoices = <T extends TaxonomyId>({ ...taxonomyState }: TaxonomyChoicesProps<T>) => {
  const dispatch = useAppDispatch()

  const { hasParent, taxonomyId, exclusive, entities, termTree, ids, selections, filtered, search, showCount, termsLoaded, boolean } =
    taxonomyState
  const [localSearch, setLocalSearch] = React.useState(search || '')

  const isFiltered = search || filtered.length > 0 ? true : false
  const hasTerms = termTree.length > 0
  const showSearch = !hasParent && ids.length > 12 // soz for the magic number... seems like a good amount;
  const showLoading = showSearch && localSearch !== search // soz for the magic number... seems like a good amount;
  const Control = exclusive ? Radio : Checkbox

  const handleChange = (termId: TermId, checked: boolean) => {
    dispatch(filterTermSelected({ taxonomyId, termId, checked, reset: true }))
  }

  React.useEffect(() => {
    if (!showSearch) return
    dispatch(termSearchChanged({ taxonomyId, search: localSearch }))
  }, [dispatch, localSearch, taxonomyId, showSearch])

  const passedFilter = (termId: TermId) => {
    if (isFiltered && !filtered.includes(termId)) return false
    return true
  }

  return (
    <>
      {showSearch && (
        <Box mt={1}>
          <TextField
            type="search"
            placeholder="Search..."
            defaultValue={search}
            onChange={(e) => setLocalSearch(e.target.value)}
            size={'small'}
            sx={{ width: '100%', borderColor: 'divider' }}
          />
          {showLoading && <LinearProgress sx={{ height: '2px', mb: '-2px' }} color="secondary" />}
        </Box>
      )}

      <Scrollable
        allowVertical={!boolean && !hasParent}
        maxHeight={'min(600px, 80vh)'}
        sx={
          !hasParent && !boolean
            ? {
                border: '1px solid',
                borderColor: 'divider',
                marginY: 2,
              }
            : {}
        }
      >
        <FormGroup
          sx={{
            ...(boolean || hasParent ? { pl: 1 } : { paddingY: 1, paddingX: 2 }),
            maxWidth: '100%',
            display: 'block',
          }}
        >
          {isFiltered && filtered.length === 0 && <Typography color={'grey.600'}>{`No results for '${search}'`}</Typography>}

          {!hasTerms && !boolean && !termsLoaded && <SkeletonTerms />}

          {hasTerms &&
            termTree.map(({ id: termId, children }, i) => {
              if (!passedFilter(termId)) return null

              const term = entities[termId]

              if (!term) return null

              const { name: label, count } = term

              // Check if this term is a match (it might be one of its children)
              const filterMatch = !search || label.toLowerCase().includes(search.toLowerCase())

              const labelText = showCount && count ? `${label} (${count})` : label
              const checked = selections.includes(termId)
              const control = <Control checked={checked} onChange={(e, v) => handleChange(termId, v)} />

              const childTree: TermTree = (checked || isFiltered) && children ? children : []

              return (
                <React.Fragment key={i}>
                  <FormControlLabel
                    sx={{ width: '100%', opacity: filterMatch ? 1 : 0.5 }}
                    // componentsProps={{ typography: typeStyle.singleLine }}
                    label={labelText}
                    control={control}
                  />

                  {childTree && (
                    <Collapse in={checked || isFiltered}>
                      <TaxonomyChoices
                        {...{
                          search,
                          hasParent: true,
                          ...taxonomyState,
                          termTree: childTree,
                        }}
                      />
                    </Collapse>
                  )}
                </React.Fragment>
              )
            })}
        </FormGroup>
      </Scrollable>
    </>
  )
}

export default TaxonomyChoices
